import { createServerFn } from "@tanstack/react-start";
import { createAuthenticatedApiConfig } from "@/lib/utils.server";
import {
  createOrganisation,
  createCommonContact,
  createServiceContact,
  getCommonContacts,
  getOrganisation,
  getOrganisationPredefinedTexts,
  getOrganisations,
  getOrganisationSettings,
  getOrganisationUserLabels,
  getServiceContacts,
  CommonContactResponseReadable,
  ServiceContactResponseReadable,
  updateCommonContact,
  updateServiceContact,
  deleteServiceContact,
  deleteCommonContact,
  updateOrganisationPredefinedTexts,
  deleteOrganisation,
  updateOrganisation,
  updateOrganisationSettings,
  deleteAllOrganisationSettings,
  getOrganisationCallscreening,
  updateOrganisationCallscreening,
  testOrganisationCallscreening,
  updateOrganisationUserLabels,
  deleteOrganisationSetting,
} from "@/lib/mma";
import {
  OrganisationReadable,
  OrganisationResponseReadable,
  PredefinedTextsResponse,
  SettingListResponse,
  UserLabelList,
  CallscreeningResponse,
  Callscreening,
} from "@/lib/mma/types.gen";
import { collectErrorMessages } from "@/lib/utils.server";

//TODO: Standardize the SubmenuItem type so that it can be used across all components
export const getOrganisationsFn = createServerFn({ method: "GET" }).handler(async (): Promise<any> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getOrganisations(config);
  console.log("<====== getOrganisationsFn response: ", response);
  if (!response.data) {
    throw new Error(collectErrorMessages(response.error) || "Failed to get organisations");
  }
  const organisations = response.data.data || [];

  return organisations.map((organisation: any) => ({
    id: organisation.id,
    name: organisation.name,
    href: `/organisations/${organisation.id}`,
    dropdown: [
      { id: `${organisation.id}-settings`, name: "Settings", href: `/organisations/${organisation.id}/settings` },
      {
        id: `${organisation.id}-contact-list`,
        name: "Contact list",
        href: `/organisations/${organisation.id}/contact-list`,
      },
      {
        id: `${organisation.id}-predefined-texts`,
        name: "Predefined texts",
        href: `/organisations/${organisation.id}/predefined-texts`,
      },
      {
        id: `${organisation.id}-users`,
        name: "Users",
        href: `/organisations/${organisation.id}/users`,
      },
      {
        id: `${organisation.id}-rename`,
        name: "Rename",
        href: `/organisations/${organisation.id}/rename`,
      },
      {
        id: `${organisation.id}-user-labels`,
        name: "User labels",
        href: `/organisations/${organisation.id}/user-labels`,
      },
      {
        id: `${organisation.id}-emails`,
        name: "Emails",
        href: `/organisations/${organisation.id}/emails`,
      },
      {
        id: `${organisation.id}-call-screening`,
        name: "Call screening",
        href: `/organisations/${organisation.id}/call-screening`,
      },
      {
        id: `${organisation.id}-delete`,
        name: "Delete",
        onClick: "delete",
        color: "red-500",
        parent: { name: organisation.name, id: organisation.id },
      },
    ],
  }));
});

//There are settings that rely on the query key being exactly "organisations", so don't change it unless it is changed in settings. See settings-fields.tsx for more details.
export const organisationsQueryOptions = () => ({
  queryKey: ["organisations"],
  queryFn: async () => {
    try {
      return await getOrganisationsFn();
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        return;
      }
      throw e;
    }
  },
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
  retry: false,
});

export const createOrganisationFn = createServerFn({ method: "POST" })
  .inputValidator((data: { name: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await createOrganisation({
      ...config,
      body: {
        name: data.name,
      },
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to create organisation");
    }
    return response.data;
  });

export const getOrganisationFn = createServerFn({ method: "GET" })
  .inputValidator((d: { orgId: number }) => d)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOrganisation({
      ...config,
      path: {
        id: Number(data.orgId),
      },
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get organisation");
    }
    console.log("response.data.data", response.data.data);
    return response.data.data;
  });

export function organisationQueryOptions(orgId: number) {
  return {
    queryKey: ["organisation", orgId],
    queryFn: () => getOrganisationFn({ data: { orgId } }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: "always" as const,
    refetchOnReconnect: false,
    retry: false,
  };
}

export const getPredefinedTextsFn = createServerFn({ method: "GET" })
  .inputValidator((d: { orgId: number }) => d)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOrganisationPredefinedTexts({
      ...config,
      path: {
        id: Number(data.orgId),
      },
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get predefined texts");
    }
    return response.data.data;
  });

export function predefinedTextsQueryOptions(orgId: number) {
  return {
    queryKey: ["predefinedTexts", orgId],
    queryFn: () => getPredefinedTextsFn({ data: { orgId } }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: "always" as const,
    refetchOnReconnect: false,
    retry: false,
  };
}

export const getOrganisationUserLabelsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }): Promise<any> => {
    const config = await createAuthenticatedApiConfig();
    console.log(
      "====> Sending get organisation user labels request to",
      config.baseUrl + "/organisations/" + data.orgId + "/user-labels"
    );
    const response = await getOrganisationUserLabels({
      ...config,
      path: { id: data.orgId },
    });
    console.log("<==== getOrganisationUserLabelsFn response", response);
    return response.data?.data || [];
  });

export const organisationUserLabelsQueryOptions = (orgId: number) => ({
  queryKey: ["organisationUserLabels", orgId],
  queryFn: () => getOrganisationUserLabelsFn({ data: { orgId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
  retry: false,
});

export const fetchOrganisationSettingsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOrganisationSettings({ ...config, path: { id: Number(data.orgId) } });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get organisation settings");
    }
    return {
      settings: response.data.data,
    };
  });

export const getCommonContactsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getCommonContacts({ ...config, path: { id: Number(data.orgId) } });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get common contacts");
    }
    return response.data.data || [];
  });

export const getServiceContactsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getServiceContacts({ ...config, path: { id: Number(data.orgId) } });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get service contacts");
    }
    return response.data.data || [];
  });

export const commonContactsQueryOptions = (orgId: number) => ({
  queryKey: ["commonContacts", orgId],
  queryFn: () => getCommonContactsFn({ data: { orgId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
  retry: false,
});

export const serviceContactsQueryOptions = (orgId: number) => ({
  queryKey: ["serviceContacts", orgId],
  queryFn: () => getServiceContactsFn({ data: { orgId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
  retry: false,
});

export const createCommonContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string; payload: any }) => data)
  .handler(async ({ data }): Promise<{ commonContact: CommonContactResponseReadable }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createCommonContact({ ...config, body: data.payload, path: { id: Number(data.id) } });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to create common contact");
    }
    return { commonContact: response.data };
  });

export const updateCommonContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; payload: any }) => data)
  .handler(async ({ data }): Promise<{ commonContact: CommonContactResponseReadable }> => {
    const config = await createAuthenticatedApiConfig();
    const { id: contactId, ...bodyPayload } = data.payload;
    const response = await updateCommonContact({
      ...config,
      path: { id: Number(data.orgId), contactId: Number(contactId) },
      body: bodyPayload,
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update common contact");
    }
    return { commonContact: response.data };
  });

export const createServiceContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; payload: any }) => data)
  .handler(async ({ data }): Promise<{ serviceContact: ServiceContactResponseReadable }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createServiceContact({ ...config, body: data.payload, path: { id: Number(data.orgId) } });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to create service contact");
    }
    return { serviceContact: response.data };
  });

export const updateServiceContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; payload: any }) => data)
  .handler(async ({ data }): Promise<{ serviceContact: ServiceContactResponseReadable }> => {
    const config = await createAuthenticatedApiConfig();
    // Extract contactId and remove it from payload
    const { id: contactId, ...bodyPayload } = data.payload;
    const response = await updateServiceContact({
      ...config,
      path: { id: Number(data.orgId), contactId: Number(contactId) },
      body: bodyPayload,
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update service contact");
    }
    return { serviceContact: response.data };
  });

export const deleteServiceContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; contactId: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteServiceContact({
      ...config,
      path: { id: Number(data.orgId), contactId: Number(data.contactId) },
    });
    if (!response.data) {
      throw new Error("Failed to delete service contact");
    }
    return { success: true };
  });

export const deleteCommonContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; contactId: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteCommonContact({
      ...config,
      path: { id: Number(data.orgId), contactId: Number(data.contactId) },
    });
    if (!response.data) {
      throw new Error("Failed to delete common contact");
    }
    return { success: true };
  });

export const updatePredefinedTextsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<{ predefinedTexts: PredefinedTextsResponse }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisationPredefinedTexts({
      ...config,
      body: data.payload,
      path: { id: Number(data.orgId) },
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update predefined texts");
    }
    return { predefinedTexts: response.data || {} };
  });

export const deleteOrganisationFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteOrganisation({ ...config, path: { id: data.orgId } });
    if (!response.data) {
      throw new Error("Failed to delete organisation");
    }
    return { success: true };
  });

export const updateOrganisationFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<{ organisation: OrganisationReadable }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisation({ ...config, path: { id: Number(data.orgId) }, body: data.payload });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update organisation");
    }
    return { organisation: response.data?.data || {} };
  });

export const organisationSettingsQueryOptions = (id: number) => ({
  queryKey: ["organisation-settings", id],
  queryFn: () => fetchOrganisationSettingsFn({ data: { orgId: id } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
  retry: false,
});

export const updateOrganisationSettingsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<{ organisationSettings: SettingListResponse }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisationSettings({
      ...config,
      path: { id: data.orgId },
      body: data.payload,
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update organisation settings");
    }
    return { organisationSettings: response.data || {} };
  });

export const resetOrganisationSettingsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }): Promise<void> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteAllOrganisationSettings({ ...config, path: { id: data.orgId } });
    if (!response.data) {
      throw new Error("Failed to reset organisation settings");
    }
    return;
  });

export const getOrganisationCallscreeningFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    console.log("=======> getOrganisationCallscreeningFn data: ", data);
    const response = await getOrganisationCallscreening({ ...config, path: { id: Number(data.orgId) } });
    console.log("<====== getOrganisationCallscreeningFn response: ", response.data?.data);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get organisation callscreening rulesets");
    }
    return response.data.data;
  });

export const organisationCallscreeningQueryOptions = (orgId: number) => ({
  queryKey: ["organisation-callscreening", orgId],
  queryFn: () => getOrganisationCallscreeningFn({ data: { orgId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
  retry: false,
});

export const updateOrganisationCallscreeningFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<CallscreeningResponse> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisationCallscreening({
      ...config,
      body: data.payload,
      path: { id: Number(data.orgId) },
    });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update organisation callscreening");
    }
    return response.data || {};
  });

export const testOrganisationCallscreeningFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    console.log("=======> testOrganisationCallscreeningFn data: ", data);
    const response = await testOrganisationCallscreening({
      ...config,
      body: data.payload,
      path: { id: Number(data.orgId) },
    });
    console.log("<====== testOrganisationCallscreeningFn response: ", response);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to test callscreening");
    }
    return response.data.data;
  });

export const updateOrganisationUserLabelsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: UserLabelList }) => data)
  .handler(async ({ data }): Promise<{ organisationUserLabels: UserLabelList }> => {
    const config = await createAuthenticatedApiConfig();
    console.log(
      "====> Sending update organisation user labels request to",
      config.baseUrl + "/organisations/" + data.orgId + "/user-labels",
      "with data",
      data.payload
    );
    const response = await updateOrganisationUserLabels({ ...config, path: { id: data.orgId }, body: data.payload });
    console.log("<==== updateOrganisationUserLabelsFn response", response);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update organisation user labels");
    }
    return { organisationUserLabels: response.data?.data || [] };
  });

export const clearOrganisationOverrideFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number; setting: string }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteOrganisationSetting({ ...config, path: { name: data.setting, id: data.id } });
    if (!response.data) {
      throw new Error(collectErrorMessages((response as any).error) || "Failed to clear organisation override");
    }
    return { success: true };
  });
